import React from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import CardNE from '../../../../../shared/components/CardNE/CardNE';
import CardStatusNE from '../../../../../shared/components/CardStatusNE/CardStatusNE';
import { LOAD_BALANCE_ROUTE } from '../../../../../shared/constants/auth';
import { IRewardsFileValidation } from '../../../../../shared/interfaces/rewards';
import { TRANSLATION_POINTS_BY_FILE } from '../../../../../shared/translations/rewards';
import { toLocaleNumberFormat } from '../../../../../shared/utils/utils';

import './ShippingDetailCard.scss';

interface ShippingDetailCardProps {
  currentPointsInfo: IRewardsFileValidation;
  hasBalance: boolean;
  missingBalance: string;
}

const ShippingDetailCard = ({ currentPointsInfo, hasBalance, missingBalance }: ShippingDetailCardProps) => {
  const { t } = useTranslation();

  return (
    <CardNE className="justify-between gap-6">
      <h3 className="ne-font-body-lg font-bold m-0">{t(TRANSLATION_POINTS_BY_FILE.details.title)}</h3>
      <div className="flex flex-row justify-between w-full">
        <div className="flex flex-column">
          <h4 className="ne-font-body-sm font-bold m-0 py-2">
            {t(TRANSLATION_POINTS_BY_FILE.details.header.collaborators)}
          </h4>
          {currentPointsInfo.orders.map((detail) => (
            <p key={uuid()} className="ne-font-body-sm m-0 py-2">
              {detail.members.length > 1
                ? t(TRANSLATION_POINTS_BY_FILE.details.values.collaboratorsPlural, {
                    collaborators: detail.members.length
                  })
                : t(TRANSLATION_POINTS_BY_FILE.details.values.collaborators, { collaborators: detail.members.length })}
            </p>
          ))}
        </div>
        <div className="flex flex-column">
          <h4 className="ne-font-body-sm font-bold m-0 py-2">{t(TRANSLATION_POINTS_BY_FILE.details.header.points)}</h4>

          {currentPointsInfo.orders.map((detail) => (
            <p key={uuid()} className="ne-font-body-sm m-0 py-2">
              {Number(detail.points) > 1
                ? t(TRANSLATION_POINTS_BY_FILE.details.values.pointsPlural, {
                    points: toLocaleNumberFormat(Number(detail.points))
                  })
                : t(TRANSLATION_POINTS_BY_FILE.details.values.points, {
                    points: toLocaleNumberFormat(Number(detail.points))
                  })}
            </p>
          ))}
        </div>
        <div className="flex flex-column">
          <h4 className="ne-font-body-sm font-bold m-0 py-2">{t(TRANSLATION_POINTS_BY_FILE.details.header.total)}</h4>

          {currentPointsInfo.orders.map((detail) => (
            <p key={uuid()} className="ne-font-body-sm m-0 py-2">
              {Number(detail.points) * detail.members.length > 1
                ? t(TRANSLATION_POINTS_BY_FILE.details.values.pointsPlural, {
                    points: toLocaleNumberFormat(Number(detail.points) * detail.members.length)
                  })
                : t(TRANSLATION_POINTS_BY_FILE.details.values.points, {
                    points: toLocaleNumberFormat(Number(detail.points) * detail.members.length)
                  })}
            </p>
          ))}
        </div>
      </div>
      <div className="shipping-detail-total">
        <div>{t(TRANSLATION_POINTS_BY_FILE.details.sendTotal)}</div>
        <div>
          {Number(currentPointsInfo.totalAmount) > 1
            ? t(TRANSLATION_POINTS_BY_FILE.details.values.pointsPlural, {
                points: toLocaleNumberFormat(Number(currentPointsInfo.totalAmount))
              })
            : t(TRANSLATION_POINTS_BY_FILE.details.values.points, {
                points: toLocaleNumberFormat(Number(currentPointsInfo.totalAmount))
              })}
        </div>
      </div>
      {!hasBalance && (
        <CardStatusNE
          type="error"
          message={
            <Trans
              i18nKey={TRANSLATION_POINTS_BY_FILE.details.insufficientPoints}
              components={{ 1: <Link to={{ pathname: LOAD_BALANCE_ROUTE }} />, balance: missingBalance }}
            />
          }
        />
      )}
    </CardNE>
  );
};

export default ShippingDetailCard;
